import { ValueAddedOP } from '@cemex-core/cmx-api';
import { Store } from '@ngrx/store';
import { getCalendars } from '@ordersnproducts/modules/order-builder/reducers';
import { ValueAddedCategoryCode } from '@ordersnproducts/modules/shared/services/experiences.service';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { map, take } from 'rxjs/operators';
import { UntypedFormGroup } from '@angular/forms';
import { SpecFieldNames } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-names.enum';

/**
 * get the value added category corresponding to the refrigerant
 * case 1: refrigerant is ice
 * case 2: refrigerant is ice
 * case 3: refrigerant is nitrogen
 * @param tempControlFlag indicates the refrigerant
 */
export function getValueAddedTargetCategory(tempControlFlag: number) {
  if (tempControlFlag === 1 || tempControlFlag === 2) {
    return ValueAddedCategoryCode.ICE;
  } else if (tempControlFlag === 3) {
    return ValueAddedCategoryCode.NITROGEN;
  }
  return '';
}

/**
 * get the value added corresponding to the refrigerant
 * case 1: refrigerant is ice
 * case 2: refrigerant is ice
 * case 3: refrigerant is nitrogen
 * @param store store
 * @param vaps list of value added products
 * @deprecated use getRefrigerantValueAdded instead
 */
export function getRefrigerant(store: Store<any>, vaps: ValueAddedOP[]) {
  const calendar$ = store
    .select(getCalendars)
    .pipe(map((calendars) => _safe(() => calendars[0].tempControlFlag, undefined)));
  let targetVap = '';
  calendar$.pipe(take(1)).subscribe((tempControlFlag) => {
    targetVap = getValueAddedTargetCategory(tempControlFlag);
  });
  const refrigerant = _safe(() => vaps.find((x) => x.valueAddedCategoryCode === targetVap), undefined);
  return refrigerant;
}

/**
 * Retrieves the refrigerant value-added object from a list of value-added objects
 * based on the specified temperature control flag.
 *
 * @param vaps - An array of value-added objects (`ValueAddedOP[]`) to search through.
 * @param tempControlFlag - A numeric flag indicating the temperature control category.
 * case 1: refrigerant is ice
 * case 2: refrigerant is ice
 * case 3: refrigerant is nitrogen
 * @returns The refrigerant value-added object if found, otherwise `undefined`.
 */
export function getRefrigerantValueAdded(vaps: ValueAddedOP[], tempControlFlag: number) {
  const targetVap = getValueAddedTargetCategory(tempControlFlag);
  const refrigerant = _safe(() => vaps.find((x) => x.valueAddedCategoryCode === targetVap), undefined);
  return refrigerant;
}


/**
 * Filters the provided array of ValueAddedOP objects to return only those that require temperature control,
 * excluding those with a valueAddedCategoryCode of ICE or NITROGEN.
 *
 * @param vaps - An array of ValueAddedOP objects to be filtered.
 * @returns An array of ValueAddedOP objects that require temperature control and are not categorized as ICE or NITROGEN.
 */
export function filterRetardantValueAdded(vaps: ValueAddedOP[]) {
  const refrigerant = _safe(() => vaps.filter((x) =>
    x.isTemperatureControl
    && ![ValueAddedCategoryCode.ICE, ValueAddedCategoryCode.NITROGEN].includes(x.valueAddedCategoryCode as ValueAddedCategoryCode),
  ), []);
  return refrigerant;
}

/**
 * Validates the summer set value in the form based on the temperature under value and value added options.
 *
 * @param form - The form group containing the fields to validate.
 * @param valueAddeds - An object containing value added options indexed by form ID.
 * @returns A boolean indicating whether the summer set value is valid.
 */
export function validateSummerSet(form: UntypedFormGroup, valueAddeds: { [formId: string]: ValueAddedOP[] }) {
  const summerSet = _safe(() => form.get(SpecFieldNames.SUMMER_SET));
  const id = _safe(() => form.get(SpecFieldNames.ID).value);
  const temperatureUnder = _safe(() => form.get(SpecFieldNames.TEMPERATURE_UNDER));

  if (temperatureUnder && !temperatureUnder.disabled && temperatureUnder.value) {
    const summerSetValue = summerSet && summerSet.value;
    const temperature = _safe(() => valueAddeds[id].find((addedValue) => addedValue.valueAddedCode === summerSetValue));
    if (!temperature) {
      return false;
    }
  }
  return true;
}
